import React, { useId } from 'react';
import { Modal } from 'reactstrap';
import { useIntl } from 'react-intl';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faXmark } from '@fortawesome/pro-light-svg-icons/faXmark';
import { faArrowsRotate } from '@fortawesome/pro-light-svg-icons/faArrowsRotate';
import { faArrowUpRightFromSquare } from '@fortawesome/pro-light-svg-icons/faArrowUpRightFromSquare';
import type { PublishedBrief } from 'fym-shared/src/releases/types';
import { TypedMemo } from '../../common/types';
import { SMLButton } from '../components/primitives/SMLButton';
import { WhatsNewBody } from './WhatsNewBody';
import { whatsNewMessages as m } from './whatsNewMessages';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the panel; each part uses its own slice of it */
import styles from './WhatsNew.module.scss';
/* eslint-enable css-modules/no-unused-class */

export type WhatsNewMode = 'after' | 'available';

interface Props {
  mode: WhatsNewMode;
  brief: PublishedBrief;
  /** The version of this bundle. The update-available subtitle names it. */
  currentVersion: string;
  changelogHref: string;
  /** "Got it", "Later", the X, Esc and the scrim. */
  onClose: () => void;
  /** "Reload" (update available only). */
  onReload: () => void;
}

/**
 * The What's new panel (design whats-new a1-a5): a modal over the app on a blurred scrim, a
 * bottom sheet under 640 px. The head and the foot stay; the body scrolls. After an update it has
 * "Got it"; when a newer version is ready it has "Later" and "Reload". It never reloads by itself.
 */
const WhatsNewPanelComponent: React.FC<Props> = ({ mode, brief, currentVersion, changelogHref, onClose, onReload }) => {
  const intl = useIntl();
  const titleId = useId();
  const isAfter = mode === 'after';

  return (
    <Modal
      isOpen
      toggle={onClose}
      centered
      labelledBy={titleId}
      className={styles.dialog}
      contentClassName={styles.content}
      backdropClassName={styles.scrim}
    >
      <div className={styles.rule} />
      <div className={styles.head}>
        <button type="button" className={styles.close} onClick={onClose} aria-label={intl.formatMessage(m.close)}>
          <FontAwesomeIcon icon={faXmark} />
        </button>
        <span className={styles.kicker}>
          <i className={styles.kickerDot} />
          {intl.formatMessage(isAfter ? m.kickerAfter : m.kickerAvailable)}
        </span>
        <h2 id={titleId} className={styles.version}>
          {brief.version}
        </h2>
        <p className={styles.subtitle}>
          {isAfter
            ? intl.formatMessage(m.subtitleAfter, { count: brief.entryCount })
            : intl.formatMessage(m.subtitleAvailable, { current: currentVersion })}
        </p>
      </div>
      <WhatsNewBody entries={brief.entries} />
      <div className={styles.foot}>
        <a className={styles.link} href={changelogHref} target="_blank" rel="noopener noreferrer">
          {intl.formatMessage(m.seeAll)}
          <FontAwesomeIcon icon={faArrowUpRightFromSquare} />
        </a>
        <span className={styles.grow} />
        {isAfter ? (
          <SMLButton variant="primary" onClick={onClose}>
            {intl.formatMessage(m.gotIt)}
          </SMLButton>
        ) : (
          <>
            <SMLButton onClick={onClose}>{intl.formatMessage(m.later)}</SMLButton>
            <SMLButton variant="primary" icon={<FontAwesomeIcon icon={faArrowsRotate} />} onClick={onReload}>
              {intl.formatMessage(m.reload)}
            </SMLButton>
          </>
        )}
      </div>
    </Modal>
  );
};

export const WhatsNewPanel = TypedMemo(WhatsNewPanelComponent);
